import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, Home, Image as ImageIcon, Film } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const REVIEW_KEY = "nico";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function MediaItem({ m, idx, total, onChange, onDelete, onReorder, onHomepage }) {
  const [d, setD] = useState(m);
  const [saving, setSaving] = useState(false);
  useEffect(() => setD(m), [m.id]);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const cls = "w-full border border-sand-200 rounded-lg px-3 py-2 text-sm";
  const src = `${BACKEND}${m.url}`;

  const save = async () => {
    if (!d.alt_en.trim() || !d.alt_es.trim()) { toast.error("Alt text is required in both languages."); return; }
    setSaving(true);
    try {
      await api.put(`/admin/review-media/${m.id}`, {
        alt_en: d.alt_en, alt_es: d.alt_es, caption_en: d.caption_en || "", caption_es: d.caption_es || "",
      });
      toast.success("Saved");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleVisible = async () => {
    try { await api.put(`/admin/review-media/${m.id}`, { visible: !m.visible }); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadVtt = async (lang, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("lang", lang);
    try { await api.post(`/admin/review-media/${m.id}/captions`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${lang.toUpperCase()} captions uploaded`); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid={`media-item-${m.id}`}>
      <div className="flex gap-4">
        <div className="w-32 shrink-0">
          {m.kind === "photo" ? (
            <img src={src} alt={m.alt_en} className="h-24 w-32 rounded-lg object-cover border border-sand-200" />
          ) : (
            <video src={src} className="h-24 w-32 rounded-lg object-cover border border-sand-200" muted preload="metadata" />
          )}
          <div className="mt-1 flex items-center gap-1 text-xs text-charcoal-400">
            {m.kind === "photo" ? <ImageIcon size={12} /> : <Film size={12} />} {m.kind} · #{idx + 1}
          </div>
        </div>
        <div className="flex-1 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid={`media-alt-en-${m.id}`} className={cls} placeholder="Alt text (EN) *" value={d.alt_en} onChange={(e) => set("alt_en", e.target.value)} />
            <input data-testid={`media-alt-es-${m.id}`} className={cls} placeholder="Alt text (ES) *" value={d.alt_es} onChange={(e) => set("alt_es", e.target.value)} />
            <input className={cls} placeholder="Caption (EN)" value={d.caption_en || ""} onChange={(e) => set("caption_en", e.target.value)} />
            <input className={cls} placeholder="Caption (ES)" value={d.caption_es || ""} onChange={(e) => set("caption_es", e.target.value)} />
          </div>
          {m.kind === "video" && (
            <div className="flex flex-wrap gap-3 text-xs text-charcoal-500">
              <label className="cursor-pointer underline">EN captions (.vtt){m.captions_en ? " ✓" : ""}<input type="file" accept=".vtt" className="hidden" onChange={(e) => uploadVtt("en", e.target.files?.[0])} /></label>
              <label className="cursor-pointer underline">ES captions (.vtt){m.captions_es ? " ✓" : ""}<input type="file" accept=".vtt" className="hidden" onChange={(e) => uploadVtt("es", e.target.files?.[0])} /></label>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid={`media-save-${m.id}`} onClick={save} disabled={saving} className="bg-terracotta text-white rounded-full px-4 py-1.5 text-sm">{saving ? "Saving…" : "Save"}</button>
        <button data-testid={`media-visible-${m.id}`} onClick={toggleVisible} className="inline-flex items-center gap-1 border rounded-full px-3 py-1.5 text-sm">
          {m.visible ? <><Eye size={14} /> Visible</> : <><EyeOff size={14} /> Hidden</>}
        </button>
        {m.kind === "photo" && (
          <button data-testid={`media-homepage-${m.id}`} onClick={() => onHomepage(m)} className={`inline-flex items-center gap-1 border rounded-full px-3 py-1.5 text-sm ${m.homepage ? "bg-sage-light border-sage text-charcoal-800" : ""}`}>
            <Home size={14} /> {m.homepage ? "On homepage card" : "Use on homepage card"}
          </button>
        )}
        <button data-testid={`media-up-${m.id}`} onClick={() => onReorder(idx, -1)} disabled={idx === 0} className="border rounded-full p-1.5 disabled:opacity-30"><ArrowUp size={14} /></button>
        <button data-testid={`media-down-${m.id}`} onClick={() => onReorder(idx, 1)} disabled={idx === total - 1} className="border rounded-full p-1.5 disabled:opacity-30"><ArrowDown size={14} /></button>
        <button data-testid={`media-delete-${m.id}`} onClick={() => onDelete(m)} className="ml-auto inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-full px-3 py-1.5 text-sm"><Trash2 size={14} /> Delete</button>
      </div>
    </Card>
  );
}

export default function ReviewsMediaManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ alt_en: "", alt_es: "", caption_en: "", caption_es: "" });
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/admin/review-media?review_key=${REVIEW_KEY}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) { toast.error("Choose a file first."); return; }
    if (!form.alt_en.trim() || !form.alt_es.trim()) { toast.error("Alt text is required in English and Spanish."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("review_key", REVIEW_KEY);
    fd.append("alt_en", form.alt_en);
    fd.append("alt_es", form.alt_es);
    fd.append("caption_en", form.caption_en);
    fd.append("caption_es", form.caption_es);
    setUploading(true);
    setProgress(0);
    try {
      await api.post("/admin/review-media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || e.loaded))),
      });
      toast.success("Uploaded");
      setForm({ alt_en: "", alt_es: "", caption_en: "", caption_es: "" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); setProgress(0); }
  };

  const del = async (m) => {
    if (!window.confirm(`Delete this ${m.kind}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/review-media/${m.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const reorder = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try { await api.post("/admin/review-media/reorder", { ids: next.map((x) => x.id) }); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const setHomepage = async (m) => {
    try { await api.post(`/admin/review-media/${m.id}/homepage`, { homepage: !m.homepage }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cls = "w-full border border-sand-200 rounded-lg px-3 py-2 text-sm";
  return (
    <div data-testid="reviews-media-manager">
      <SectionTitle title="Reviews Media" subtitle="Photos & videos attached to a review (currently: Nico). Stored in secure object storage; location data is stripped and images are optimized automatically." />

      <Card className="p-5 space-y-3 mb-6">
        <p className="text-sm font-medium text-charcoal-700">Upload new media</p>
        <label data-testid="media-file-input" className="flex items-center gap-2 text-sm border border-dashed border-sand-300 rounded-xl px-4 py-3 cursor-pointer hover:bg-sand-50">
          <Upload size={16} /> {file ? file.name : "Choose a photo (JPG, PNG, WebP, HEIC) or video (MP4, MOV, max 100MB)"}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="media-new-alt-en" className={cls} placeholder="Alt text (EN) — required" value={form.alt_en} onChange={(e) => setForm({ ...form, alt_en: e.target.value })} />
          <input data-testid="media-new-alt-es" className={cls} placeholder="Alt text (ES) — required" value={form.alt_es} onChange={(e) => setForm({ ...form, alt_es: e.target.value })} />
          <input className={cls} placeholder="Caption (EN) — optional" value={form.caption_en} onChange={(e) => setForm({ ...form, caption_en: e.target.value })} />
          <input className={cls} placeholder="Caption (ES) — optional" value={form.caption_es} onChange={(e) => setForm({ ...form, caption_es: e.target.value })} />
        </div>
        {uploading && (
          <div className="h-2 w-full rounded-full bg-sand-200 overflow-hidden" data-testid="media-progress">
            <div className="h-full bg-terracotta transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
        <button data-testid="media-upload-btn" onClick={upload} disabled={uploading} className="bg-terracotta text-white rounded-full px-5 py-2 text-sm disabled:opacity-60">
          {uploading ? `Uploading… ${progress}%` : "Upload"}
        </button>
      </Card>

      {items.length === 0 ? (
        <p className="text-sm text-charcoal-400" data-testid="media-empty">No media yet. Uploaded photos and videos will appear here and on Nico's story.</p>
      ) : (
        <div className="space-y-4">
          {items.map((m, i) => (
            <MediaItem key={m.id} m={m} idx={i} total={items.length} onChange={load} onDelete={del} onReorder={reorder} onHomepage={setHomepage} />
          ))}
        </div>
      )}
    </div>
  );
}
